/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useRef } from "react";

import { gql, useLazyQuery, useQuery } from "@apollo/client";

import { CREDIT_NOTE_FRAGMENT, CreditNoteFragment } from "graphql/fragments";
import { QueryCreditNotesArgs } from "graphql/types";

import { PaginatedPayload } from "./shared/types";

const CREDIT_NOTES = gql`
  query (
    $page: Int
    $perPage: Int
    $filter: CreditNotesFilter
    $sortBy: CreditNotesSortByEnum
  ) {
    creditNotes(
      page: $page
      perPage: $perPage
      filter: $filter
      sortBy: $sortBy
    ) {
      exportPath
      items {
        ...CreditNoteFragment
      }
      total
    }
  }
  ${CREDIT_NOTE_FRAGMENT}
`;

export interface CreditNotesQuery {
  creditNotes: PaginatedPayload<CreditNoteFragment>;
}

type QueryArgs = Pick<
  QueryCreditNotesArgs,
  "page" | "perPage" | "filter" | "sortBy"
>;

export const useCreditNotesQuery = (args: QueryArgs = {}) => {
  const { data, loading: isLoading } = useQuery<CreditNotesQuery, QueryArgs>(
    CREDIT_NOTES,
    {
      variables: { ...args },
    }
  );

  const creditNotes = data?.creditNotes.items?.filter(Boolean) ?? [];

  return {
    creditNotes,
    isLoading,
  };
};

export const useCreditNotesLazyQuery = () => {
  const [getCreditNotesQuery, { data, loading: isLoading, called }] =
    useLazyQuery<CreditNotesQuery, QueryArgs>(CREDIT_NOTES, {
      fetchPolicy: "network-only",
    });

  const creditNotes = data?.creditNotes.items?.filter(Boolean) ?? [];
  const total = data?.creditNotes.total ?? 0;

  const resolveRef =
    useRef<(creditNotes?: CreditNoteFragment[]) => void>(undefined);

  useEffect(() => {
    if (called && !isLoading && resolveRef.current) {
      resolveRef.current(creditNotes);
      resolveRef.current = undefined;
    }
  }, [creditNotes, called, isLoading]);

  const getCreditNotes = async (args?: QueryArgs) => {
    const variables = { ...args };

    getCreditNotesQuery({ variables });

    return new Promise<CreditNoteFragment[] | undefined>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  return { getCreditNotes, creditNotes, total, isLoading, called };
};
